import { AppLogger } from '../appLogger/';
import {
  generateChallenge,
  generateRandomString,
  launchUri,
  urlSafeEncode,
} from './authClient.helpers';
import { AuthEvents } from './authEvents';
import JWT from './authToken';
import { REFRESH_TOKEN_BUFFER, STORAGE_PREFIX } from './constants';

type RegistrationResponse = {
  client_id?: string;
  error_description?: string;
};

type TokenResponse = {
  // access_token: string; unused as it's same as id_token
  id_token: string;
  refresh_token: string;
  token_type: string;
};

type RefreshTokenResponse = {
  AuthenticationResult: {
    IdToken: string;
    AccessToken: string;
    TokenType: string;
  };
  ChallengeParameters: any;
};

class AuthClient {
  // Singleton pattern - only one instance of AuthClient can exist
  private static instance: AuthClient | null = null;

  private _clientId: string = ''; // Initialize _clientId property
  private _codeVerifier: string = ''; // PKCE code verifier
  private _idToken: JWT | undefined; // ID token
  private _refreshToken: string = ''; // Refresh token
  private _state: string = ''; // State value for CSRF protection
  private _customState: string = ''; // Custom state to be passed to the authorization server e.g subdomains
  private _codeChallenge: string = ''; // PKCE code challenge
  private _code: string = ''; // The authorization code is a temporary credential that is exchanged for an id token after login.
  private _tokenRefreshPromise: Promise<JWT | null> | null = null; // Token refresh promise

  // public
  public authEndpoint: string = '';
  public redirectUri: string = '';
  public staticConfig: any;

  private _hasConfigured: boolean = false; // We need to throw an error if the client is not configured

  private constructor() {
    this.staticConfig = {
      code_challenge_method: 'S256',
      identity_provider: 'COGNITO',
      response_type: 'code',
      scope: 'openid',
    };
  }

  // Getter and setters
  get clientId() {
    return this._clientId || this.getAuthStorage('local', 'clientId');
  }

  set clientId(value: string) {
    this.setAuthStorage('local', 'clientId', value);
    this._clientId = value;
  }

  get codeVerifier() {
    return this._codeVerifier || this.getAuthStorage('session', 'codeVerifier');
  }

  set codeVerifier(value: string) {
    this.setAuthStorage('session', 'codeVerifier', value);
    this._codeVerifier = value;
  }

  get idToken() {
    if (this._idToken) {
      return this._idToken;
    }

    return new JWT(this.getAuthStorage('local', 'idToken'));
  }

  set idToken(value: JWT) {
    this.setAuthStorage('local', 'idToken', value.jwtString);
    this._idToken = value;
  }

  get refreshToken() {
    return this._refreshToken || this.getAuthStorage('local', 'refreshToken');
  }

  set refreshToken(value: string) {
    this.setAuthStorage('local', 'refreshToken', value);
    this._refreshToken = value;
  }

  private setAuthStorage(
    storageType: 'local' | 'session',
    key: string,
    value: string,
  ) {
    const keyName = `${STORAGE_PREFIX}${key}`;

    if (storageType === 'local') {
      localStorage.setItem(keyName, value);
    } else if (storageType === 'session') {
      sessionStorage.setItem(keyName, value);
    }
  }
  private getAuthStorage(storageType: string, key: string): string {
    const keyName = `${STORAGE_PREFIX}${key}`;

    if (storageType === 'local') {
      return localStorage.getItem(keyName) ?? '';
    } else {
      return sessionStorage.getItem(keyName) ?? '';
    }
  }

  private handleError(errorMessage: string): never {
    AppLogger.error(errorMessage);
    AuthEvents.publish('auth:error', { error: errorMessage });
    throw new Error(errorMessage);
  }

  private _ensureConfigured() {
    if (!this._hasConfigured) {
      this.handleError(
        'AuthClient is not configured. Please call configure() first.',
      );
    }
  }

  /**
   * Register a client with the Oauth2 server
   * @param clientName
   * @param isSSOX
   * @returns { client_id: string, error_description: string }
   */
  async register(
    clientName: string,
    isSSOX: boolean, // SS0X = Single Sign On Between Apps
  ): Promise<RegistrationResponse> {
    this._ensureConfigured();
    const url = `${this.authEndpoint}/register`;
    const body = {
      client_name: clientName,
      // The redirect_uri 'usl://sso-between-apps' is a special redirect uri value for SSOX
      redirect_uris: isSSOX ? ['usl://sso-between-apps'] : [this.redirectUri],
      grant_types: ['authorization_code', 'refresh_token'],
      scope: this.staticConfig.scope,
    };

    const registrationRequest = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const registrationResponse: RegistrationResponse =
      await registrationRequest.json();
    const { client_id } = registrationResponse;
    if (!registrationRequest.ok) {
      this.handleError(
        `Failed to register client: ${registrationResponse.error_description}`,
      );
    }
    if (client_id) {
      this.clientId = client_id;
    }

    return registrationResponse;
  }

  async authorize(): Promise<void> {
    this._ensureConfigured();
    const generatedState = generateRandomString(true);

    const state = this._customState
      ? `${generatedState}-${urlSafeEncode(this._customState)}`
      : generatedState;

    const codeVerifier = generateRandomString(false, 128);
    const codeChallenge = await generateChallenge(codeVerifier);

    this._codeChallenge = codeChallenge;
    this.codeVerifier = codeVerifier;
    this._state = state;

    const queryString = Object.entries({
      client_id: this.clientId,
      code_challenge: codeChallenge,
      redirect_uri: this.redirectUri,
      state: state,
      ...this.staticConfig,
    })
      .map(([k, v]: any) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

    const URL = `${this.authEndpoint}/oauth2/authorize?${queryString}`;
    await launchUri(URL);
  }

  /**
   * Authorize to the Oauth2 server with a One Time Token (OTT)
   * This is used to authorize the user without the need for a password
   * For example when the user wants to move from Zeus to Hermes without logging in again
   * @param token
   * @returns { code: string, tenancy_hash: string }
   */
  async authorizeWithOTT(
    token: string,
  ): Promise<{ code: string; tenancy_hash: string }> {
    this._ensureConfigured();
    const codeVerifier = generateRandomString(false, 128);
    const codeChallenge = await generateChallenge(codeVerifier);

    this._codeChallenge = codeChallenge;
    this.codeVerifier = codeVerifier;
    this.redirectUri = 'usl://sso-between-apps';

    const body = {
      client_id: this.clientId,
      code_challenge: codeChallenge,
      code_challenge_method: this.staticConfig.code_challenge_method,
      token: token,
      token_type: 'x-usl-ott',
      scope: this.staticConfig.scope,
    };

    const url = `${this.authEndpoint}/oauth2/authorize`;

    const authorizeWithOTTRequest = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    try {
      const authorizeWithOTTRequestResponse =
        await authorizeWithOTTRequest.json();
      const { code, tenancy_hash } = authorizeWithOTTRequestResponse;
      if (code) {
        this._code = code;
      }

      return { code, tenancy_hash };
    } catch (e) {
      this.handleError(`Failed to authorize with OTT: ${e}`);
    }
  }

  async registerAndAuthorize(
    clientName: string,
    isSSOX: boolean = false,
  ): Promise<void> {
    this._ensureConfigured();
    try {
      await this.register(clientName, isSSOX);
    } catch (e) {
      this.handleError(`Failed to register client: ${e}`);
    }
    try {
      await this.authorize();
    } catch (e) {
      this.handleError(`Failed to authorize client: ${e}`);
    }
  }

  /**
   * Perform a series of checks to determine if the user is authenticated
   * // 1. If we have a code in the URL, exchange it for a token
   * // 2. If we don't have a code in the URL, check if we have a refresh token in storage and refresh if necessary
   * // 3. If we don't have a code in the URL and we don't have tokens in storage, do nothing
   * @param authEndpoint
   * @returns
   */
  async checkIfUserIsAuthenticated(authEndpoint?: string) {
    this._ensureConfigured();

    AuthEvents.publish('auth:checkIfUserIsAuthenticated', {
      authEndpoint: this.authEndpoint,
    });

    const params = new URLSearchParams(new URL(window.location.href).search);
    const paramCode = params.get('code');
    const effectiveCode = paramCode || this._code;
    this._code = effectiveCode;

    // 1. If we have a code in the URL, exchange it for a token
    if (effectiveCode) {
      AuthEvents.publish(
        'auth:info',
        'Code found in URL, exchanging for token',
      );
      try {
        const tokenResponse: TokenResponse =
          await this.exchangeCodeForToken(effectiveCode);

        if (tokenResponse) {
          AuthEvents.publish('auth:info', 'Code exchanged for token');

          const { id_token, refresh_token } = tokenResponse;

          this.setAuthStorage('local', 'idToken', id_token);
          this.setAuthStorage('local', 'refreshToken', refresh_token);

          AuthEvents.publish('auth:signIn', { tokenResponse });
          return tokenResponse;
        }
      } catch (e) {
        this.handleError(`Failed to exchange code for token ${e}`);
      }
    } else if (this.refreshToken && this.idToken) {
      AuthEvents.publish(
        'auth:info',
        'Refresh token found, calling smartRefresh',
      );

      // 2. If we don't have a code in the URL, check if we have a refreshToken in storage and refresh if necessary
      return await this.smartRefresh();
    }
  }

  /**
   * Attempts to refresh the token if it is about to expire
   * If the token is valid (taken into account the buffer time), it will return the existing token
   * @returns JWT | null
   */
  public async smartRefresh(): Promise<JWT | null> {
    this._ensureConfigured();
    let tokenToReturn: JWT | null = null;
    try {
      // Check if we have an idToken or refreshToken in storage

      if (this.refreshToken) {
        let hasIdTokenExpired = false;

        const exp = this.idToken.getExpiration();

        if (!exp) {
          this.handleError('Failed to get the expiration time of the token');
        }

        // Subtract 30 seconds from the expiry time to create a buffer
        // This will allow us to refresh the token before it expires
        const adjustedExpiryTime = exp - REFRESH_TOKEN_BUFFER;

        // We consider the token expired if the current time is greater than the adjusted expiry time
        if (Date.now() >= adjustedExpiryTime) {
          hasIdTokenExpired = true;
        }

        if (hasIdTokenExpired) {
          // If the token is about to expire, refresh it
          const newIdToken = await this.refreshIdToken();
          tokenToReturn = newIdToken;
        } else {
          // If the token is not about to expire, resolve with the existing token
          tokenToReturn = this.idToken;
        }
        return tokenToReturn;
      } else {
        // In the case of no refresh token we return null continuing the flow
        return null;
      }
    } catch (error) {
      this.handleError(`Error in smartRefresh: ${error}`);
    }
  }

  async exchangeCodeForToken(code: string): Promise<TokenResponse> {
    this._ensureConfigured();
    AuthEvents.publish('auth:exchangeCodeForToken', { code });

    const url = `${this.authEndpoint}/oauth2/token`;
    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('code', code);
    params.append('redirect_uri', this.redirectUri);
    params.append('client_id', this.clientId ?? '');
    params.append('code_verifier', this.codeVerifier);

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });

    const tokenResponseData: TokenResponse = await response.json();
    const { id_token, refresh_token } = tokenResponseData;

    this.idToken = new JWT(id_token);
    this.refreshToken = refresh_token;

    if (!response.ok) {
      this.handleError(`Failed to exchange code for token ${response.status}`);
    }
    return tokenResponseData;
  }

  public async refreshIdToken(): Promise<JWT | null> {
    this._ensureConfigured();
    if (!this._tokenRefreshPromise) {
      this._tokenRefreshPromise = (async () => {
        try {
          const url = `${this.authEndpoint}/`;
          const headers = {
            'cache-control': 'no-store',
            'content-type': 'application/x-amz-json-1.1',
            'x-amz-target': 'AWSCognitoIdentityProviderService.InitiateAuth',
            'x-amz-user-agent': 'aws-amplify/5.0.4 auth framework/0',
          };
          const body = {
            ClientId: this.clientId,
            AuthFlow: 'REFRESH_TOKEN_AUTH',
            AuthParameters: {
              REFRESH_TOKEN: this.refreshToken,
              DEVICE_KEY: null,
            },
          };

          const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            this.handleError(`HTTP error! status: ${response.status}`);
          }

          const tokenRefreshResponse: RefreshTokenResponse =
            await response.json();

          const responseIdToken =
            tokenRefreshResponse?.AuthenticationResult?.IdToken;

          if (!responseIdToken) {
            this.handleError('AuthenticationResult or IdToken is missing');
          }

          const newIdToken = new JWT(responseIdToken);

          this.idToken = newIdToken;
          return newIdToken;
        } catch (error) {
          this._tokenRefreshPromise = null;
          this.deleteAuthSession();
          this.handleError(`Couldn't refresh IdToken: ${error}`);
        } finally {
          this._tokenRefreshPromise = null;
        }
      })();
    }
    return this._tokenRefreshPromise;
  }

  public async signOut() {
    if (this.clientId && this.redirectUri) {
      const logoutUrl = new URL(`${this.authEndpoint}/logout`);
      logoutUrl.searchParams.append('client_id', this.clientId);
      logoutUrl.searchParams.append('logout_uri', this.redirectUri);

      this.deleteAuthSession();
      try {
        await fetch(logoutUrl.toString(), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (e) {
        this.handleError(`Failed to sign out: ${e}`);
      }
    }
  }

  public deleteAuthSession() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('_auth')) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Before using the AuthClient, it has to be configure it with the authEndpoint and redirectUri
   * The authEndpoint is the URL of the Oauth2 server ie. https://global.enterprise.test.lifeworks.com
   * The redirectUri is the URL to redirect to after the user has logged in
   * @param { authEndpoint: string, redirectUri: string }
   */
  public configure({
    authEndpoint,
    redirectUri,
  }: {
    authEndpoint: string;
    redirectUri?: string;
  }) {
    if (!authEndpoint) {
      this.handleError('authEndpoint is required');
    }

    // Default the redirectUri to the current window location
    // For Next.js we need explicitly check if windows is defined as it can cause an error in the build process
    if (!redirectUri && typeof window !== 'undefined' && window.location) {
      const newUri = window.location.protocol + '//' + window.location.host;
      this.redirectUri = newUri;

      AppLogger.info(
        'AuthClient.redirectUri not configured with falling back to default: ',
        newUri,
      );
    } else if (redirectUri) {
      this.redirectUri = redirectUri;
    }

    this.authEndpoint = authEndpoint;
    this._hasConfigured = true;
  }

  public static getInstance(): AuthClient {
    if (!AuthClient.instance) {
      AuthClient.instance = new AuthClient();
    }
    return AuthClient.instance;
  }
}
// Create a single instance of Oauth2Client
const oauth2Client = AuthClient.getInstance();

export { oauth2Client as AuthClient };
